import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { LegacyOpsBanner } from "../components/LegacyOpsBanner";
import { AuthRequired } from "../components/AuthRequired";
import { WithdrawForm } from "../components/WithdrawForm";
import { apiCall, ApiHttpError, RouteNotFoundError } from "../api/client";

// Withdraw affiliate page — full lifecycle, browser-driven.
// Companion to BE PR #139.
//
// Three sections:
//   1. Buckets — GET /v2/admin/bff/escrow/affiliate-withdrawable
//      One card per enrolled affiliate with `total_rewards_minor > 0`
//      (Policy A). Shows off-chain accounting AND on-chain
//      withdrawable so the operator can diagnose the gap.
//   2. Create proposal — per-bucket POST /v2/admin/bff/escrow/
//      withdraw-affiliate/proposals. BE re-resolves the affiliate
//      wallet from D1; browser only passes affiliateIdentityId.
//   3. Execute — pre-existing WithdrawForm, kept for the
//      execute-by-approved-proposalId flow. BE payload-match guard
//      ensures request token/affiliate/amount match the approved
//      proposal byte-for-byte before broadcast.

interface AffiliateBucket {
  affiliateIdentityId: string;
  referralCode: string | null;
  affiliateWalletAddress: string | null;
  walletSource: "payout_address" | "primary_wallet" | null;
  totalRewardsMinor: number;
  totalRewardsHumanReadable: string;
  withdrawableBaseUnits: string;
  withdrawableHumanReadable: string;
  canCreateProposal: boolean;
  reason: string | null;
}

interface AffiliateWithdrawableResponse {
  ok: boolean;
  chainId: number;
  network: string;
  escrow: string;
  token: string;
  tokenSymbol: string;
  tokenDecimals: number;
  paused: boolean | null;
  killSwitchTripped: boolean;
  killSwitchReason: string | null;
  buckets: AffiliateBucket[];
  count: number;
}

interface CreateAffiliateProposalResponse {
  ok: boolean;
  proposalId: string;
  actionDigest: string;
  payloadHash: string;
  proposal: {
    id: string;
    thresholdRequired: number;
    status: string;
    expiresAt: string;
  };
  bucket: {
    affiliateIdentityId: string;
    affiliateWalletAddress: string;
    walletSource: "payout_address" | "primary_wallet";
    withdrawableBaseUnits: string;
  };
}

function shortId(s: string, head = 12, tail = 6): string {
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

function ReasonBadge({ reason }: { reason: string | null }) {
  if (reason === null) {
    return (
      <span style={{ color: "#0a9444", fontSize: 11 }}>can create proposal</span>
    );
  }
  return (
    <span style={{ color: "#d23f3f", fontSize: 11 }}>
      cannot: <code>{reason}</code>
    </span>
  );
}

function BucketCard({
  bucket,
  onCreated,
}: {
  bucket: AffiliateBucket;
  onCreated: (proposalId: string) => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState(bucket.withdrawableBaseUnits);
  const [ttlSecStr, setTtlSecStr] = useState("28800");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateAffiliateProposalResponse | null>(null);

  useEffect(() => {
    setAmount(bucket.withdrawableBaseUnits);
  }, [bucket.withdrawableBaseUnits]);

  const amountValid = /^(?:0|[1-9][0-9]*)$/.test(amount) && amount !== "0";
  const amountWithinBudget = (() => {
    if (!amountValid) return false;
    try {
      return BigInt(amount) <= BigInt(bucket.withdrawableBaseUnits);
    } catch {
      return false;
    }
  })();
  const ttlValid = /^[1-9][0-9]*$/.test(ttlSecStr) && Number(ttlSecStr) >= 60;
  const canSubmit =
    bucket.canCreateProposal && amountValid && amountWithinBudget && ttlValid && !busy;

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      const j = await apiCall<CreateAffiliateProposalResponse>(
        "/v2/admin/bff/escrow/withdraw-affiliate/proposals",
        {
          method: "POST",
          body: {
            affiliateIdentityId: bucket.affiliateIdentityId,
            amount,
            ttlSec: Number(ttlSecStr),
          },
        },
      );
      setResult(j);
      onCreated(j.proposalId);
    } catch (e) {
      if (e instanceof RouteNotFoundError) {
        setError("Route not registered on BE.");
      } else if (e instanceof ApiHttpError) {
        const bodyMsg =
          e.body !== null && typeof e.body === "object" && "message" in e.body
            ? String((e.body as { message: unknown }).message)
            : "";
        const reason =
          e.body !== null && typeof e.body === "object" && "reason" in e.body
            ? String((e.body as { reason: unknown }).reason)
            : "";
        setError(
          `HTTP ${e.status}${bodyMsg ? ` — ${bodyMsg}` : ""}${reason ? ` [${reason}]` : ""}`,
        );
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Unknown error.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        margin: "8px 0",
        padding: 12,
        border: "1px solid var(--border, #ccc)",
        background: bucket.canCreateProposal
          ? "rgba(10, 148, 68, 0.03)"
          : "transparent",
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
        <h5 style={{ fontSize: 13, margin: 0, fontFamily: "monospace" }}>
          {shortId(bucket.affiliateIdentityId)}
        </h5>
        {bucket.referralCode && (
          <span style={{ fontSize: 11, color: "var(--fg-dim)" }}>
            ref <code>{bucket.referralCode}</code>
          </span>
        )}
        <span style={{ marginLeft: "auto" }}>
          <ReasonBadge reason={bucket.reason} />
        </span>
      </div>

      <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse", margin: "8px 0" }}>
        <tbody>
          <tr>
            <td style={{ padding: "2px 4px", color: "var(--fg-dim)" }}>Wallet</td>
            <td style={{ padding: "2px 4px", fontFamily: "monospace" }}>
              {bucket.affiliateWalletAddress ?? <em>(not enrolled)</em>}
              {bucket.walletSource && (
                <span style={{ marginLeft: 8, color: "var(--fg-dim)", fontStyle: "italic" }}>
                  source: {bucket.walletSource}
                </span>
              )}
            </td>
          </tr>
          <tr>
            <td style={{ padding: "2px 4px", color: "var(--fg-dim)" }}>
              Off-chain rewards
            </td>
            <td style={{ padding: "2px 4px" }}>
              {bucket.totalRewardsHumanReadable}{" "}
              <span style={{ color: "var(--fg-dim)" }}>
                ({bucket.totalRewardsMinor} minor)
              </span>
            </td>
          </tr>
          <tr>
            <td style={{ padding: "2px 4px", color: "var(--fg-dim)" }}>
              On-chain withdrawable
            </td>
            <td style={{ padding: "2px 4px" }}>
              <strong>{bucket.withdrawableHumanReadable}</strong>{" "}
              <span style={{ color: "var(--fg-dim)" }}>
                ({bucket.withdrawableBaseUnits} base units)
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      {bucket.canCreateProposal && !result && (
        <>
          {!showForm && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              style={{
                padding: "4px 12px",
                fontSize: 12,
                background: "var(--accent, #2c7be5)",
                color: "white",
                border: "none",
                borderRadius: 3,
                cursor: "pointer",
              }}
            >
              Create multisign withdraw proposal
            </button>
          )}
          {showForm && (
            <div style={{ marginTop: 8, padding: 10, border: "1px solid var(--border-faint, #eee)" }}>
              <div style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "6px 10px", fontSize: 11 }}>
                <label style={{ alignSelf: "center" }}>Amount (base units)</label>
                <div>
                  <input
                    type="text"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={busy}
                    style={{ width: "100%", padding: "3px 6px", fontFamily: "monospace", fontSize: 11 }}
                  />
                  <div style={{ fontSize: 10, color: "var(--fg-dim)", marginTop: 2 }}>
                    Max: {bucket.withdrawableBaseUnits}{" "}
                    ({bucket.withdrawableHumanReadable})
                    {!amountValid && (
                      <span style={{ color: "#d23f3f" }}> · Must be a positive integer</span>
                    )}
                    {amountValid && !amountWithinBudget && (
                      <span style={{ color: "#d23f3f" }}> · Exceeds bucket</span>
                    )}
                  </div>
                </div>

                <label style={{ alignSelf: "center" }}>TTL (seconds)</label>
                <div>
                  <input
                    type="text"
                    value={ttlSecStr}
                    onChange={(e) => setTtlSecStr(e.target.value)}
                    disabled={busy}
                    style={{ width: 120, padding: "3px 6px", fontFamily: "monospace", fontSize: 11 }}
                  />
                  <span style={{ fontSize: 10, color: "var(--fg-dim)", marginLeft: 6 }}>
                    default 28800 · max 86400 · min 60
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => void submit()}
                  disabled={!canSubmit}
                  style={{
                    padding: "4px 12px",
                    fontSize: 12,
                    background: "var(--accent, #2c7be5)",
                    color: "white",
                    border: "none",
                    borderRadius: 3,
                    cursor: canSubmit ? "pointer" : "default",
                    opacity: canSubmit ? 1 : 0.5,
                  }}
                >
                  {busy ? "Creating…" : "Submit"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  disabled={busy}
                  style={{ padding: "4px 12px", fontSize: 12 }}
                >
                  Cancel
                </button>
              </div>
              {error && (
                <p role="alert" style={{ marginTop: 6, fontSize: 11, color: "#d23f3f" }}>
                  {error}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {result && (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            border: "1px solid #0a9444",
            background: "rgba(10, 148, 68, 0.06)",
            fontSize: 11,
          }}
        >
          <p style={{ margin: 0, color: "#0a9444", fontWeight: 600 }}>
            ✓ Proposal created
          </p>
          <p style={{ margin: "2px 0" }}>
            ID: <code>{result.proposalId}</code> · Threshold:{" "}
            {result.proposal.thresholdRequired} · Status:{" "}
            <strong>{result.proposal.status}</strong>
          </p>
          <p style={{ margin: "2px 0" }}>
            <Link
              to={`/approvals/${result.proposalId}`}
              style={{ color: "var(--accent, #2c7be5)" }}
            >
              Open multisign approval flow →
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}

function BucketsSection({
  envelope,
  loading,
  error,
  onRefresh,
  onCreated,
}: {
  envelope: AffiliateWithdrawableResponse | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  onCreated: (proposalId: string) => void;
}) {
  return (
    <section style={{ margin: "12px 0", padding: 14, border: "1px solid var(--border, #ccc)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <h4 style={{ fontSize: 13, margin: "0 0 8px 0" }}>Affiliate buckets</h4>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          style={{ marginLeft: "auto", padding: "2px 10px", fontSize: 12 }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>
      <p style={{ margin: "4px 0 8px 0", fontSize: 11, color: "var(--fg-dim)" }}>
        Bound to: <code>GET /v2/admin/bff/escrow/affiliate-withdrawable</code>. Policy A: shows
        all enrolled affiliates with off-chain rewards &gt; 0, regardless of on-chain bucket
        value.
      </p>

      {error && <p style={{ color: "#d23f3f", fontSize: 12 }}>{error}</p>}

      {envelope && (
        <>
          <p style={{ fontSize: 11, color: "var(--fg-dim)", margin: "4px 0" }}>
            Chain: <strong>{envelope.network}</strong> (id {envelope.chainId}) · Token:{" "}
            <code>{envelope.token}</code> ({envelope.tokenSymbol}) · paused:{" "}
            {envelope.paused === null ? (
              <em>unknown</em>
            ) : envelope.paused ? (
              <span style={{ color: "#d23f3f" }}>true</span>
            ) : (
              <span style={{ color: "#0a9444" }}>false</span>
            )}{" "}
            · kill-switch:{" "}
            {envelope.killSwitchTripped ? (
              <span style={{ color: "#d23f3f" }}>
                TRIPPED ({envelope.killSwitchReason ?? "unknown"})
              </span>
            ) : (
              <span style={{ color: "#0a9444" }}>untripped</span>
            )}
          </p>

          {envelope.count === 0 ? (
            <p style={{ fontSize: 12, color: "var(--fg-dim)", padding: 10 }}>
              No affiliate buckets visible. Enrolled affiliates accrue on-chain only when a
              settle() call passes their wallet as the <code>affiliate</code> argument. If a
              real referrer has an off-chain reward but no on-chain accrual, check the referrer's{" "}
              <code>identity_affiliate_index.payout_address</code> and/or{" "}
              <code>identity_wallets</code> primary wallet — see PR #139 BE docs.
            </p>
          ) : (
            <div>
              {envelope.buckets.map((b) => (
                <BucketCard
                  key={b.affiliateIdentityId}
                  bucket={b}
                  onCreated={onCreated}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

function ExecuteSection({
  envelope,
  lastCreatedProposalId,
}: {
  envelope: AffiliateWithdrawableResponse;
  lastCreatedProposalId: string | null;
}) {
  return (
    <section
      style={{
        margin: "12px 0",
        padding: 14,
        border: "1px solid var(--border, #ccc)",
        background: "rgba(210, 63, 63, 0.04)",
      }}
    >
      <h4 style={{ fontSize: 13, margin: "0 0 8px 0" }}>Execute approved proposal</h4>
      <p style={{ margin: "4px 0", fontSize: 12, color: "#d23f3f" }}>
        ⚠ This broadcasts an on-chain transaction. Only call this AFTER the proposal has reached
        status=approved (2-of-2 owner signatures via the Approvals tab). The BE payload-match
        guard refuses if request token + affiliate + amount don't match the approved proposal
        byte-for-byte.
      </p>
      <p style={{ margin: "4px 0", fontSize: 11, color: "var(--fg-dim)" }}>
        Bound to: <code>POST /v2/admin/bff/escrow/withdraw-affiliate</code>
      </p>

      {lastCreatedProposalId && (
        <p style={{ margin: "4px 0", fontSize: 12 }}>
          Last created proposal:{" "}
          <Link
            to={`/approvals/${lastCreatedProposalId}`}
            style={{ color: "var(--accent, #2c7be5)" }}
          >
            <code>{lastCreatedProposalId}</code>
          </Link>
        </p>
      )}

      <p style={{ margin: "4px 0 12px 0", fontSize: 11, color: "var(--fg-dim)" }}>
        Token (canonical): <code>{envelope.token}</code> ({envelope.tokenSymbol}). Fill{" "}
        <strong>affiliate</strong>, <strong>amount</strong>, and the approved{" "}
        <strong>proposalId</strong> below.
      </p>

      <WithdrawForm
        showAffiliate
        path="/v2/admin/bff/escrow/withdraw-affiliate"
        buttonLabel="POST withdraw-affiliate (broadcasts on-chain)"
        description="Transfers from the affiliate's on-chain bucket to the affiliate's address. Public self-withdraw is locked on V3; only owner-approved BFF execute works."
      />
    </section>
  );
}

function WithdrawAffiliateInner() {
  const [envelope, setEnvelope] = useState<AffiliateWithdrawableResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastCreatedProposalId, setLastCreatedProposalId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const j = await apiCall<AffiliateWithdrawableResponse>(
        "/v2/admin/bff/escrow/affiliate-withdrawable",
      );
      setEnvelope(j);
    } catch (e) {
      if (e instanceof RouteNotFoundError) {
        setError(
          "Route /v2/admin/bff/escrow/affiliate-withdrawable not registered. Check the BE deploy.",
        );
      } else if (e instanceof ApiHttpError) {
        setError(`HTTP ${e.status} from /v2/admin/bff/escrow/affiliate-withdrawable.`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError("Unknown error.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <p style={{ margin: "4px 0", fontSize: 12, color: "var(--fg-dim)" }}>
        Full lifecycle:{" "}
        <strong>view buckets → create proposal → approve 2/2 → execute → history</strong>. No
        HMAC in browser. BE re-resolves affiliate wallet from D1 source of truth.{" "}
        <Link to="/approvals/history" style={{ color: "var(--accent, #2c7be5)" }}>
          History →
        </Link>
      </p>

      <BucketsSection
        envelope={envelope}
        loading={loading}
        error={error}
        onRefresh={load}
        onCreated={(id) => setLastCreatedProposalId(id)}
      />

      {envelope && (
        <ExecuteSection
          envelope={envelope}
          lastCreatedProposalId={lastCreatedProposalId}
        />
      )}
    </div>
  );
}

export function WithdrawAffiliate() {
  return (
    <div>
      <PageHeader
        title="Withdraw affiliate"
        subtitle="Full lifecycle: view buckets, create multisign proposal, approve 2/2, execute. No HMAC in browser. BE re-resolves affiliate wallet from D1."
        method="GET / POST"
        path="/v2/admin/bff/escrow/affiliate-withdrawable + /withdraw-affiliate/proposals + /withdraw-affiliate"
      />
      <LegacyOpsBanner what="This is the legacy affiliate bucket sweep." />
      <AuthRequired>
        <WithdrawAffiliateInner />
      </AuthRequired>
    </div>
  );
}
